import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import ReceiptDetailModal from './components/ReceiptDetailModal';
import UpdateAllocationsModal from './components/UpdateAllocationsModal';
import { getReceipts, getReceiptDetails, updateReceipt, deleteReceipt, clearReceipt, bounceReceipt, clearReceiptState, bulkCancelReceipts, bulkBounceReceipts, bulkClearReceipts } from '../../../../redux/actions/SocietyPortal/ReceiptAction/ReceiptAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { getChartOfAccountsDropdown } from '../../../../redux/actions/SocietyPortal/JournalAction/JournalAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_RECEIPTS_CREATE, SOCIETY_PORTAL_RECEIPTS_PRINT, SOCIETY_PORTAL_RECEIPTS_BULK_PRINT, SOCIETY_PORTAL_RECEIPTS_BULK_UPLOAD } from '../../../../Routing/routes';

const STATUSES = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'CLEARED', label: 'Cleared' },
    { value: 'BOUNCED', label: 'Bounced' },
    { value: 'CANCELLED', label: 'Cancelled' },
];

const PAYMENT_MODES = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CHEQUE', label: 'Cheque' },
    { value: 'DD', label: 'Demand Draft' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
    { value: 'UPI', label: 'UPI' },
    { value: 'NEFT', label: 'NEFT' },
    { value: 'RTGS', label: 'RTGS' },
    { value: 'ONLINE', label: 'Online' },
];

const RECEIPT_TYPES = [
    { value: 'PAYMENT', label: 'Payment' },
    { value: 'ADVANCE', label: 'Advance' },
];

const Receipts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        receiptsData, receiptsPagination, loading, receiptDetails, detailsLoading,
        updateLoading, deleteLoading, clearLoading, bounceLoading, bulkCancelLoading, bulkBounceLoading, bulkClearLoading
    } = useSelector(state => state.ReceiptReducer);
    const { wingsDropdown, flatsDropdown } = useSelector(state => state.WingsAndFlatsReducers);
    const { coaDropdown } = useSelector(state => state.JournalReducer || {});

    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPaymentMode, setFilterPaymentMode] = useState('all');
    const [filterReceiptType, setFilterReceiptType] = useState('all');
    const [filterWing, setFilterWing] = useState('all');
    const [filterFlat, setFilterFlat] = useState('all');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showAllocationsModal, setShowAllocationsModal] = useState(false);
    const [allocationsReceipt, setAllocationsReceipt] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchParams, setSearchParams] = useState({ page: 1, limit: 10, society_id: societyId });

    useEffect(() => {
        return () => dispatch(clearReceiptState());
    }, [dispatch]);

    useEffect(() => {
        dispatch(getWingsDropdown({ society_id: societyId }));
        // Used to show a TDS receipt's GL account by name rather than bare code
        dispatch(getChartOfAccountsDropdown({ societyId, type: 'account' }));
    }, [dispatch, societyId]);

    useEffect(() => {
        if (filterWing && filterWing !== 'all') dispatch(getFlatsDropdown({ wing_id: filterWing }));
        setFilterFlat('all');
    }, [dispatch, filterWing]);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getReceipts({
            ...searchParams,
            search: searchQuery || undefined,
            status: filterStatus !== 'all' ? filterStatus : undefined,
            payment_mode: filterPaymentMode !== 'all' ? filterPaymentMode : undefined,
            receipt_type: filterReceiptType !== 'all' ? filterReceiptType : undefined,
            wing_id: filterWing !== 'all' ? Number(filterWing) : undefined,
            flat_id: filterFlat !== 'all' ? Number(filterFlat) : undefined,
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
        }));
    }, [dispatch, searchParams, searchQuery, filterStatus, filterPaymentMode, filterReceiptType, filterWing, filterFlat, fromDate, toDate, refreshKey]);

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'CLEARED': return 'tw-bg-emerald-100 tw-text-emerald-700';
            case 'PENDING': return 'tw-bg-amber-100 tw-text-amber-700';
            case 'BOUNCED': return 'tw-bg-red-100 tw-text-red-700';
            case 'CANCELLED': return 'tw-bg-gray-200 tw-text-gray-600';
            default: return 'tw-bg-gray-100 tw-text-gray-700';
        }
    };

    const getPaymentModeLabel = (mode) => {
        const found = PAYMENT_MODES.find(m => m.value === mode);
        return found ? found.label : mode || '-';
    };

    // A cheque or demand draft is identified by its number, which is held in
    // reference_no. Showing it with the mode saves opening the receipt to see
    // which cheque a payment came from.
    const INSTRUMENT_MODES = ['CHEQUE', 'DD'];
    const paymentModeText = (receipt) => {
        const label = getPaymentModeLabel(receipt.payment_mode);
        return INSTRUMENT_MODES.includes(receipt.payment_mode) && receipt.reference_no
            ? `${label} #${receipt.reference_no}`
            : label;
    };

    const formatCurrency = (amount) => `Rs ${parseFloat(amount || 0).toLocaleString('en-IN')}`;
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const clearFilters = () => {
        setSearchQuery(''); setFilterStatus('all'); setFilterPaymentMode('all');
        setFilterReceiptType('all'); setFilterWing('all'); setFilterFlat('all');
        setFromDate(''); setToDate('');
        setSearchParams({ ...searchParams, page: 1 });
    };

    const hasActiveFilters = searchQuery || filterStatus !== 'all' || filterPaymentMode !== 'all' || filterReceiptType !== 'all' || filterWing !== 'all' || filterFlat !== 'all' || fromDate || toDate;

    const refetchList = () => dispatch(getReceipts({
        ...searchParams,
        search: searchQuery || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        payment_mode: filterPaymentMode !== 'all' ? filterPaymentMode : undefined,
        receipt_type: filterReceiptType !== 'all' ? filterReceiptType : undefined,
        wing_id: filterWing !== 'all' ? Number(filterWing) : undefined,
        flat_id: filterFlat !== 'all' ? Number(filterFlat) : undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
    }));

    const handleView = (receipt) => {
        setSelectedReceipt(receipt);
        dispatch(getReceiptDetails({ id: receipt.receipt_id }));
        setShowDetailModal(true);
    };
    const closeModal = () => { setShowDetailModal(false); setSelectedReceipt(null); };

    // Proof view links are signed for 15 minutes, so always refetch the detail rather than cache one
    const reloadReceiptDetails = () => dispatch(getReceiptDetails({ id: selectedReceipt.receipt_id }));

    const handleReplaceProof = (file) => {
        dispatch(updateReceipt({ id: selectedReceipt.receipt_id, payment_proof: file, onSuccess: reloadReceiptDetails }));
    };

    const handleRemoveProof = () => {
        if (window.confirm('Remove the payment proof photo from this receipt?')) {
            dispatch(updateReceipt({ id: selectedReceipt.receipt_id, remove_payment_proof: true, onSuccess: reloadReceiptDetails }));
        }
    };

    const handleCancel = (receipt) => {
        if (window.confirm('Are you sure you want to cancel this receipt?')) {
            dispatch(deleteReceipt({ id: receipt.receipt_id, onSuccess: () => { refetchList(); closeModal(); } }));
        }
    };

    const handleClear = (receipt) => {
        if (window.confirm('Mark this receipt as cleared?')) {
            dispatch(clearReceipt({ id: receipt.receipt_id, onSuccess: () => { refetchList(); closeModal(); } }));
        }
    };

    const handleBounce = (receipt) => {
        const reason = window.prompt('Enter reason for bouncing this receipt:');
        if (reason) {
            dispatch(bounceReceipt({ id: receipt.receipt_id, reason, onSuccess: () => { refetchList(); closeModal(); } }));
        }
    };

    const handlePrintReceipt = (receipt) => {
        navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_RECEIPTS_PRINT.replace(':id', receipt.receipt_id)}`);
    };

    const handleEditAllocations = (receipt) => {
        dispatch(getReceiptDetails({ id: receipt.receipt_id }));
        setAllocationsReceipt(receipt);
        setShowAllocationsModal(true);
    };
    const closeAllocationsModal = () => { setShowAllocationsModal(false); setAllocationsReceipt(null); };

    const handleSelectAll = (checked) => {
        setSelectedIds(checked ? receipts.map(r => r.receipt_id) : []);
    };

    const handleSelectOne = (receiptId, checked) => {
        setSelectedIds(prev => checked ? [...prev, receiptId] : prev.filter(id => id !== receiptId));
    };

    const handleBulkPrint = () => {
        if (selectedIds.length === 0) return;
        navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_RECEIPTS_BULK_PRINT}?ids=${selectedIds.join(',')}`);
    };

    const handleBulkClear = () => {
        if (selectedIds.length === 0) return;
        if (window.confirm(`Clear ${selectedIds.length} selected receipt(s)? Only PENDING receipts will be cleared.`)) {
            dispatch(bulkClearReceipts({ receipt_ids: selectedIds, onSuccess: () => { setSelectedIds([]); refetchList(); } }));
        }
    };

    const handleBulkBounce = () => {
        if (selectedIds.length === 0) return;
        if (window.confirm(`Bounce ${selectedIds.length} selected receipt(s)? Only PENDING or CLEARED receipts can be bounced.`)) {
            dispatch(bulkBounceReceipts({ receipt_ids: selectedIds, onSuccess: () => { setSelectedIds([]); refetchList(); } }));
        }
    };

    const handleBulkCancel = () => {
        if (selectedIds.length === 0) return;
        if (window.confirm(`Cancel ${selectedIds.length} selected receipt(s)? Only PENDING or CLEARED receipts can be cancelled.`)) {
            dispatch(bulkCancelReceipts({ receipt_ids: selectedIds, onSuccess: () => { setSelectedIds([]); refetchList(); } }));
        }
    };

    const receipts = receiptsData || [];

    return (
        <>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Receipts</h1>
                    <p className="tw-text-xs tw-text-gray-500">Manage payment receipts and collections</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_RECEIPTS_BULK_UPLOAD)} className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700">
                        <Icon icon="mdi:upload-outline" /> Bulk Upload
                    </button>
                    <button onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_RECEIPTS_CREATE)} className="tw-flex tw-items-center tw-gap-1.5 tw-bg-emerald-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-emerald-700">
                        <Icon icon="mdi:plus" /> Create Receipt
                    </button>
                </div>
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.length > 0 && (
                <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-xl tw-px-4 tw-py-3 tw-mb-4 tw-flex tw-items-center tw-justify-between tw-animate-in">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-600 tw-text-white tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold">
                            {selectedIds.length}
                        </div>
                        <span className="tw-text-sm tw-font-medium tw-text-blue-900">
                            receipt{selectedIds.length > 1 ? 's' : ''} selected
                        </span>
                        <button onClick={() => setSelectedIds([])} className="tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 tw-font-medium tw-underline">
                            Clear
                        </button>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                            onClick={handleBulkClear}
                            disabled={bulkClearLoading}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-emerald-600 tw-text-white tw-rounded-lg tw-text-xs tw-font-semibold hover:tw-bg-emerald-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            <Icon icon={bulkClearLoading ? "mdi:loading" : "mdi:check-circle-outline"} className={`tw-text-sm ${bulkClearLoading ? 'tw-animate-spin' : ''}`} /> {bulkClearLoading ? 'Clearing...' : 'Clear'}
                        </button>
                        <button
                            onClick={handleBulkBounce}
                            disabled={bulkBounceLoading}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-orange-600 tw-text-white tw-rounded-lg tw-text-xs tw-font-semibold hover:tw-bg-orange-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            <Icon icon={bulkBounceLoading ? "mdi:loading" : "mdi:alert-circle-outline"} className={`tw-text-sm ${bulkBounceLoading ? 'tw-animate-spin' : ''}`} /> {bulkBounceLoading ? 'Bouncing...' : 'Bounce'}
                        </button>
                        <button
                            onClick={handleBulkPrint}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-indigo-600 tw-text-white tw-rounded-lg tw-text-xs tw-font-semibold hover:tw-bg-indigo-700 tw-transition-colors"
                        >
                            <Icon icon="mdi:printer" className="tw-text-sm" /> Print
                        </button>
                        <button
                            onClick={handleBulkCancel}
                            disabled={bulkCancelLoading}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-red-600 tw-text-white tw-rounded-lg tw-text-xs tw-font-semibold hover:tw-bg-red-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            <Icon icon={bulkCancelLoading ? "mdi:loading" : "mdi:close-circle-outline"} className={`tw-text-sm ${bulkCancelLoading ? 'tw-animate-spin' : ''}`} /> {bulkCancelLoading ? 'Cancelling...' : 'Cancel'}
                        </button>
                    </div>
                </div>
            )}

            {/* Filter Bar */}
            <div className="tw-bg-white tw-rounded-xl tw-p-3 tw-mb-6 tw-shadow-sm tw-border tw-border-gray-200">
                <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                    <div className="tw-flex-1 tw-min-w-[280px] tw-relative">
                        <Icon icon="mdi:magnify" className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-lg" />
                        <input type="text" placeholder="Search by receipt no, member or unit..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="tw-w-full tw-pl-10 tw-pr-4 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow" />
                    </div>
                    <div className="tw-relative">
                        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="tw-w-40 tw-pl-3 tw-pr-8 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-appearance-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow">
                            <option value="all">All Status</option>
                            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                        <Icon icon="mdi:chevron-down" className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none" />
                    </div>
                    <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors ${showAdvancedFilters ? 'tw-bg-blue-100 tw-text-blue-700' : 'tw-bg-gray-50 tw-text-gray-600 hover:tw-bg-gray-100 tw-border tw-border-gray-200'}`}>
                        <Icon icon="mdi:filter-variant" className="tw-text-base" /> Filters
                        {hasActiveFilters && <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-blue-600"></span>}
                    </button>
                    {hasActiveFilters && (
                        <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-red-600 tw-bg-red-50 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-red-100 tw-transition-colors">
                            <Icon icon="mdi:close" className="tw-text-base" /> Clear
                        </button>
                    )}
                    <div className="tw-flex tw-bg-gray-100 tw-p-1 tw-rounded-lg tw-border tw-border-gray-200">
                        <button onClick={() => setViewMode('grid')} className={`tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-transition-colors ${viewMode === 'grid' ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}><Icon icon="mdi:view-grid" className="tw-text-lg" /></button>
                        <button onClick={() => setViewMode('list')} className={`tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-transition-colors ${viewMode === 'list' ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}><Icon icon="mdi:view-list" className="tw-text-lg" /></button>
                    </div>
                    <div className="tw-px-2 tw-border-l tw-border-gray-200">
                        <span className="tw-text-sm tw-font-medium tw-text-gray-600">{receipts.length} <span className="tw-text-gray-400 tw-font-normal">of</span> {receiptsPagination?.total || receipts.length}</span>
                    </div>
                </div>

                {showAdvancedFilters && (
                    <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200 tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3">
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Payment Mode</label>
                            <div className="tw-relative">
                                <select value={filterPaymentMode} onChange={(e) => { setFilterPaymentMode(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }} className="tw-w-full tw-pl-3 tw-pr-8 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-appearance-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow">
                                    <option value="all">All Modes</option>
                                    {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                                <Icon icon="mdi:chevron-down" className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Receipt Type</label>
                            <div className="tw-relative">
                                <select value={filterReceiptType} onChange={(e) => { setFilterReceiptType(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }} className="tw-w-full tw-pl-3 tw-pr-8 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-appearance-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow">
                                    <option value="all">All Types</option>
                                    {RECEIPT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                                <Icon icon="mdi:chevron-down" className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Wing</label>
                            <div className="tw-relative">
                                <select value={filterWing} onChange={(e) => { setFilterWing(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }} className="tw-w-full tw-pl-3 tw-pr-8 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-appearance-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow">
                                    <option value="all">All Wings</option>
                                    {(wingsDropdown || []).map(w => <option key={w.wing_id} value={w.wing_id}>{w.title}</option>)}
                                </select>
                                <Icon icon="mdi:chevron-down" className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Flat</label>
                            <div className="tw-relative">
                                <select value={filterFlat} onChange={(e) => { setFilterFlat(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }} disabled={filterWing === 'all'} className="tw-w-full tw-pl-3 tw-pr-8 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-appearance-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                                    <option value="all">{filterWing === 'all' ? 'Select Wing First' : 'All Flats'}</option>
                                    {(flatsDropdown || []).map(f => <option key={f.flat_id} value={f.flat_id}>{f.flat_number}</option>)}
                                </select>
                                <Icon icon="mdi:chevron-down" className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">From Date</label>
                            <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }} className="tw-w-full tw-px-3 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow" />
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">To Date</label>
                            <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }} className="tw-w-full tw-px-3 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow" />
                        </div>
                    </div>
                )}
            </div>

            {loading && <div className="tw-flex tw-justify-center tw-items-center tw-py-12"><div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-emerald-600"></div></div>}

            {/* List View */}
            {!loading && viewMode === 'list' && (
                <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-overflow-hidden">
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-sm tw-text-left">
                            <thead className="tw-bg-gray-50/80 tw-border-b tw-border-gray-200">
                                <tr>
                                    <th className="tw-px-4 tw-py-3.5 tw-w-10">
                                        <input type="checkbox" checked={receipts.length > 0 && selectedIds.length === receipts.length} onChange={(e) => handleSelectAll(e.target.checked)} className="form-check-input" />
                                    </th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Receipt No</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Unit</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Member</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Payment Date</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-text-right">Amount</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Payment Mode</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {receipts.map((receipt) => {
                                    const canClear = receipt.status === 'PENDING';
                                    const canBounce = ['PENDING', 'CLEARED'].includes(receipt.status);
                                    const canCancel = ['PENDING', 'CLEARED'].includes(receipt.status);
                                    const canEditAllocations = ['PENDING', 'CLEARED'].includes(receipt.status);
                                    return (
                                        <tr key={receipt.receipt_id} className={`tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/80 tw-transition-colors ${selectedIds.includes(receipt.receipt_id) ? 'tw-bg-blue-50/50' : ''}`}>
                                            <td className="tw-px-4 tw-py-3 tw-w-10">
                                                <input type="checkbox" checked={selectedIds.includes(receipt.receipt_id)} onChange={(e) => handleSelectOne(receipt.receipt_id, e.target.checked)} className="form-check-input" />
                                            </td>
                                            <td className="tw-px-4 tw-py-3">
                                                <div className="tw-flex tw-items-center tw-gap-3">
                                                    <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-blue-50 tw-text-blue-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                        <Icon icon="mdi:receipt-text-outline" className="tw-text-lg" />
                                                    </div>
                                                    <div>
                                                        <span className="tw-font-medium tw-text-gray-900">{receipt.receipt_number || '-'}</span>
                                                        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{receipt.receipt_type === 'PAYMENT' ? 'Payment' : receipt.receipt_type || '-'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="tw-px-4 tw-py-3"><p className="tw-font-medium tw-text-gray-900">{receipt.flat?.wing?.title}-{receipt.flat?.flat_number || '-'}</p></td>
                                            <td className="tw-px-4 tw-py-3">
                                                <p className="tw-font-medium tw-text-gray-900">{receipt.entity?.first_name ? `${receipt.entity.first_name} ${receipt.entity.last_name || ''}`.trim() : (receipt.entity?.company_name || '-')}</p>
                                            </td>
                                            <td className="tw-px-4 tw-py-3 tw-text-gray-600">{formatDate(receipt.payment_date)}</td>
                                            <td className="tw-px-4 tw-py-3 tw-text-right">
                                                <p className="tw-font-semibold tw-text-emerald-600">{formatCurrency(receipt.amount)}</p>
                                                {parseFloat(receipt.tds_deducted) > 0 && <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">TDS: {formatCurrency(receipt.tds_deducted)}</p>}
                                            </td>
                                            <td className="tw-px-4 tw-py-3">
                                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-700">
                                                    <Icon icon={receipt.payment_mode === 'CASH' ? 'mdi:cash' : receipt.payment_mode === 'CHEQUE' ? 'mdi:checkbook' : receipt.payment_mode === 'UPI' ? 'mdi:cellphone' : 'mdi:bank'} className="tw-text-sm tw-text-gray-400" />
                                                    {paymentModeText(receipt)}
                                                </span>
                                                {INSTRUMENT_MODES.includes(receipt.payment_mode)
                                                    ? (receipt.instrument_date && <p className="tw-text-[11px] tw-text-gray-400 tw-mt-0.5">Dated {formatDate(receipt.instrument_date)}</p>)
                                                    : (receipt.reference_no && <p className="tw-text-[11px] tw-text-gray-400 tw-mt-0.5">Ref: {receipt.reference_no}</p>)}
                                            </td>
                                            <td className="tw-px-4 tw-py-3">
                                                <span className={`tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-leading-none tw-inline-flex tw-items-center ${getStatusColor(receipt.status)}`}>{receipt.status}</span>
                                            </td>
                                            <td className="tw-px-4 tw-py-3">
                                                <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5">
                                                    <button onClick={() => handleView(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors" title="View Details"><Icon icon="mdi:eye-outline" className="tw-text-lg" /></button>
                                                    <button onClick={() => handlePrintReceipt(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-purple-50 tw-text-gray-400 hover:tw-text-purple-600 tw-transition-colors" title="Print Receipt"><Icon icon="mdi:printer-outline" className="tw-text-lg" /></button>
                                                    {canEditAllocations && <button onClick={() => handleEditAllocations(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-purple-50 tw-text-gray-400 hover:tw-text-purple-600 tw-transition-colors" title="Edit Allocations"><Icon icon="mdi:format-list-checks" className="tw-text-lg" /></button>}
                                                    {canClear && <button onClick={() => handleClear(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-green-50 tw-text-gray-400 hover:tw-text-green-600 tw-transition-colors" title="Clear" disabled={clearLoading}><Icon icon="mdi:check-circle-outline" className="tw-text-lg" /></button>}
                                                    {canBounce && <button onClick={() => handleBounce(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-orange-50 tw-text-gray-400 hover:tw-text-orange-600 tw-transition-colors" title="Bounce" disabled={bounceLoading}><Icon icon="mdi:alert-circle-outline" className="tw-text-lg" /></button>}
                                                    {canCancel && <button onClick={() => handleCancel(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-600 tw-transition-colors" title="Cancel" disabled={deleteLoading}><Icon icon="mdi:close-circle-outline" className="tw-text-lg" /></button>}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {receipts.length === 0 && (
                        <div className="tw-text-center tw-py-12 tw-bg-gray-50/50">
                            <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-16 tw-h-16 tw-rounded-full tw-bg-gray-100 tw-mb-4"><Icon icon="mdi:receipt-text-outline" className="tw-text-3xl tw-text-gray-400" /></div>
                            <h3 className="tw-font-medium tw-text-gray-900 tw-mb-1">No Receipts Found</h3>
                            <p className="tw-text-gray-500 tw-text-sm">Try adjusting your filters or search query.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Grid View */}
            {!loading && viewMode === 'grid' && (
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                    {receipts.map((receipt) => {
                        const canClear = receipt.status === 'PENDING';
                        const canBounce = ['PENDING', 'CLEARED'].includes(receipt.status);
                        const canCancel = ['PENDING', 'CLEARED'].includes(receipt.status);
                        const canEditAllocations = ['PENDING', 'CLEARED'].includes(receipt.status);
                        return (
                            <div key={receipt.receipt_id} className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-p-4 tw-transition-shadow hover:tw-shadow-md">
                                <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                                    <div className="tw-flex tw-items-center tw-gap-3">
                                        <div className="tw-w-11 tw-h-11 tw-rounded-lg tw-bg-blue-50 tw-text-blue-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"><Icon icon="mdi:receipt-text-outline" className="tw-text-xl" /></div>
                                        <div>
                                            <p className="tw-font-bold tw-text-gray-900">{receipt.receipt_number || '-'}</p>
                                            <p className="tw-text-xs tw-text-gray-500 tw-font-medium">{receipt.receipt_type === 'PAYMENT' ? 'Payment' : receipt.receipt_type || '-'}</p>
                                        </div>
                                    </div>
                                    <span className={`tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-leading-none ${getStatusColor(receipt.status)}`}>{receipt.status}</span>
                                </div>
                                <div className="tw-space-y-2.5 tw-py-3 tw-border-t tw-border-b tw-border-gray-100">
                                    <div className="tw-flex tw-items-center tw-justify-between tw-text-sm"><span className="tw-text-gray-500">Unit:</span><span className="tw-font-medium tw-text-gray-900">{receipt.flat?.wing?.title}-{receipt.flat?.flat_number || '-'}</span></div>
                                    <div className="tw-flex tw-items-center tw-justify-between tw-text-sm"><span className="tw-text-gray-500">Member:</span><span className="tw-font-medium tw-text-gray-900">{receipt.entity?.first_name ? `${receipt.entity.first_name} ${receipt.entity.last_name || ''}`.trim() : (receipt.entity?.company_name || '-')}</span></div>
                                    <div className="tw-flex tw-items-center tw-justify-between tw-text-sm"><span className="tw-text-gray-500">Date:</span><span className="tw-text-gray-900">{formatDate(receipt.payment_date)}</span></div>
                                    <div className="tw-flex tw-items-center tw-justify-between tw-text-sm"><span className="tw-text-gray-500">Mode:</span><span className="tw-font-medium tw-text-gray-900">{paymentModeText(receipt)}</span></div>
                                    {receipt.reference_no && <div className="tw-flex tw-items-center tw-justify-between tw-text-sm"><span className="tw-text-gray-500">Ref:</span><span className="tw-text-gray-700 tw-text-right tw-max-w-[140px] tw-truncate" title={receipt.reference_no}>{receipt.reference_no}</span></div>}
                                </div>
                                <div className="tw-py-3">
                                    <div className="tw-flex tw-items-center tw-justify-between"><span className="tw-text-sm tw-text-gray-500">Amount:</span><span className="tw-font-bold tw-text-emerald-600 tw-text-base">{formatCurrency(receipt.amount)}</span></div>
                                    {parseFloat(receipt.tds_deducted) > 0 && (
                                        <div className="tw-flex tw-items-center tw-justify-between tw-mt-1"><span className="tw-text-xs tw-text-gray-400">TDS:</span><span className="tw-text-xs tw-text-gray-600">{formatCurrency(receipt.tds_deducted)}</span></div>
                                    )}
                                </div>
                                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-3 tw-border-t tw-border-gray-100">
                                    <button onClick={() => handleView(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors" title="View"><Icon icon="mdi:eye-outline" className="tw-text-lg" /></button>
                                    <button onClick={() => handlePrintReceipt(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-purple-50 tw-text-gray-400 hover:tw-text-purple-600 tw-transition-colors" title="Print"><Icon icon="mdi:printer-outline" className="tw-text-lg" /></button>
                                    {canEditAllocations && <button onClick={() => handleEditAllocations(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-purple-50 tw-text-gray-400 hover:tw-text-purple-600 tw-transition-colors" title="Edit Allocations"><Icon icon="mdi:format-list-checks" className="tw-text-lg" /></button>}
                                    {canClear && <button onClick={() => handleClear(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-green-50 tw-text-gray-400 hover:tw-text-green-600 tw-transition-colors" title="Clear" disabled={clearLoading}><Icon icon="mdi:check-circle-outline" className="tw-text-lg" /></button>}
                                    {canBounce && <button onClick={() => handleBounce(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-orange-50 tw-text-gray-400 hover:tw-text-orange-600 tw-transition-colors" title="Bounce" disabled={bounceLoading}><Icon icon="mdi:alert-circle-outline" className="tw-text-lg" /></button>}
                                    {canCancel && <button onClick={() => handleCancel(receipt)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-600 tw-transition-colors" title="Cancel" disabled={deleteLoading}><Icon icon="mdi:close-circle-outline" className="tw-text-lg" /></button>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {!loading && receiptsPagination && receiptsPagination.total > 0 && (
                <PaginationLimit currentPage={receiptsPagination.page || searchParams.page} totalRecords={receiptsPagination.total || 0} limit={searchParams.limit} handleLimitChange={(e) => setSearchParams({ ...searchParams, limit: parseInt(e.target.value), page: 1 })} handlePageChange={(page) => setSearchParams({ ...searchParams, page })} />
            )}

            {showDetailModal && selectedReceipt && (
                <ReceiptDetailModal
                    receipt={receiptDetails || selectedReceipt}
                    glAccounts={coaDropdown || []}
                    loading={detailsLoading}
                    onClose={closeModal}
                    onPrint={() => handlePrintReceipt(selectedReceipt)}
                    onClear={() => handleClear(selectedReceipt)}
                    onBounce={() => handleBounce(selectedReceipt)}
                    onCancel={() => handleCancel(selectedReceipt)}
                    clearLoading={clearLoading}
                    bounceLoading={bounceLoading}
                    cancelLoading={deleteLoading}
                    onReplaceProof={handleReplaceProof}
                    onRemoveProof={handleRemoveProof}
                    onReloadProof={reloadReceiptDetails}
                    proofLoading={updateLoading}
                />
            )}

            {showAllocationsModal && allocationsReceipt && (
                <UpdateAllocationsModal
                    receipt={receiptDetails || allocationsReceipt}
                    onClose={closeAllocationsModal}
                    onSuccess={refetchList}
                />
            )}
        </>
    );
};

export default Receipts;
